import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button } from '@enamelai/design-system'
import { generateFormPDF } from '@utils/formPdfGenerator.util'
import { useAppSelector } from '@store/hooks'
import { buildClinicPdfHeaderPayload } from '@utils/clinic.util'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import { logger } from '@utils/safeLogger'

import './CreateFormHeader.css'

interface CreateFormHeaderProps {
  showActions: boolean
  mode?: 'create' | 'edit' | 'view'
  previewToggleRef?: React.MutableRefObject<{
    isPreviewMode: boolean
    togglePreview: () => void
  } | null>
  onSaveTemplate?: () => void
  onSaveAsDraft?: () => void
  saveInProgress?: boolean
  onEditTemplate?: () => void
  /** Leaves the builder without saving (Discard). */
  onDiscard?: () => void
  /** Approves the template that is open in view mode. */
  onApproveTemplate?: () => void
  /** Starts a blank template (view mode). */
  onCreateNewForm?: () => void
  templateStatus?: FormTemplateStatus
  onSendToPatient?: () => void
  /** Assign needs a saved, approved template. */
  canAssign?: boolean
}

const CreateFormHeader: React.FC<CreateFormHeaderProps> = ({
  showActions,
  mode = 'edit',
  previewToggleRef,
  onSaveTemplate,
  onSaveAsDraft,
  saveInProgress = false,
  onEditTemplate,
  onDiscard,
  onApproveTemplate,
  onCreateNewForm,
  templateStatus,
  onSendToPatient,
  canAssign = false,
}) => {
  const navigate = useNavigate()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const clinicPdfHeader = useMemo(
    () => buildClinicPdfHeaderPayload(selectedClinic),
    [selectedClinic],
  )
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (previewToggleRef?.current) {
      setIsPreviewMode(previewToggleRef.current.isPreviewMode)
    }

    intervalRef.current = setInterval(() => {
      if (previewToggleRef?.current) {
        setIsPreviewMode((prev) => {
          const current = previewToggleRef.current?.isPreviewMode ?? false
          return current !== prev ? current : prev
        })
      }
    }, 100)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [previewToggleRef])

  const handlePreviewClick = (): void => {
    if (previewToggleRef?.current) {
      previewToggleRef.current.togglePreview()
      setTimeout(() => {
        if (previewToggleRef?.current) {
          setIsPreviewMode(previewToggleRef.current.isPreviewMode)
        }
      }, 0)
    }
  }

  const handleGeneratePDF = async (): Promise<void> => {
    try {
      const formPdfElement = document.getElementById('form-pdf-output')
      if (!formPdfElement) {
        console.error('Form PDF output element not found')
        return
      }

      const titleElement = formPdfElement.querySelector('.form-preview-title')
      const formTitle = titleElement?.textContent || 'Form'

      await generateFormPDF({
        element: formPdfElement,
        filename: `${formTitle.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
        clinicPdfHeader,
      })
    } catch (error) {
      logger.error('Error generating PDF:', error)
    }
  }

  const goToForms = (): void => navigate('/form-builder')
  const goToTemplates = (): void => navigate('/form-builder/templates')

  const isBuilderMode = mode !== 'view'
  const breadcrumbItems = isBuilderMode
    ? [
        { id: 'forms', label: 'Forms', onClick: goToForms },
        { id: 'templates', label: 'Templates', onClick: goToTemplates },
        { id: 'builder', label: 'Form Builder' },
      ]
    : [
        { id: 'forms', label: 'Forms', onClick: goToForms },
        { id: 'templates', label: 'Templates' },
      ]

  return (
    <div className="create-form-header">
      <Breadcrumbs
        className="form-builder-breadcrumbs"
        items={breadcrumbItems}
        showBackIcon
        onBackClick={isBuilderMode ? goToTemplates : goToForms}
      />
      {showActions && (
        <div className="create-form-header-right">
          {isBuilderMode ? (
            <>
              <Button
                size="md"
                type="button"
                variant="secondary"
                destructive
                onClick={onDiscard}
                disabled={!onDiscard}
              >
                Discard
              </Button>
              <Button
                size="md"
                type="button"
                variant="secondary"
                destructive={isPreviewMode}
                onClick={handlePreviewClick}
              >
                {isPreviewMode ? 'Exit Preview' : 'Preview'}
              </Button>
              <Button
                size="md"
                type="button"
                variant="secondary"
                onClick={onSendToPatient}
                disabled={!canAssign}
              >
                Assign to patient
              </Button>
              <Button
                size="md"
                type="button"
                variant="secondary"
                onClick={onSaveAsDraft}
                disabled={saveInProgress}
              >
                Save as draft
              </Button>
              <Button
                size="md"
                type="button"
                variant="primary"
                onClick={onSaveTemplate}
                disabled={saveInProgress}
              >
                Save template
              </Button>
            </>
          ) : (
            <>
              {/* Pending templates only offer Approve / Edit / Create new. */}
              {templateStatus === FormTemplateStatus.Approved && (
                <Button
                  size="md"
                  type="button"
                  variant="secondary"
                  onClick={handleGeneratePDF}
                  disabled={!isPreviewMode}
                >
                  Generate PDF
                </Button>
              )}
              {templateStatus === FormTemplateStatus.Approved ? (
                <Button
                  size="md"
                  type="button"
                  variant="secondary"
                  onClick={onSendToPatient}
                >
                  Assign to patient
                </Button>
              ) : (
                <Button
                  size="md"
                  type="button"
                  variant="secondary"
                  onClick={onApproveTemplate}
                  disabled={!onApproveTemplate}
                >
                  Approve this Form
                </Button>
              )}
              <Button
                size="md"
                type="button"
                variant="secondary"
                onClick={onEditTemplate}
                disabled={!onEditTemplate}
              >
                Edit form
              </Button>
              <Button
                size="md"
                type="button"
                variant="primary"
                onClick={onCreateNewForm}
                disabled={!onCreateNewForm}
              >
                Create new form
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default CreateFormHeader
